import { IntlShape, MessageDescriptor, defineMessages } from 'react-intl';
import { Music24Capabilities, RenderLane, isMusic24TeamPlanTier } from 'fym-common/src/license/music24Plans';

export type Music24FeatureGroup = 'release' | 'promote' | 'measure' | 'team';

/** The group order of the plan cards and the compare table (design a). */
export const MUSIC24_FEATURE_GROUPS: Music24FeatureGroup[] = ['release', 'promote', 'measure', 'team'];

const groupMessages = defineMessages({
  release: { id: 'app.sml.plans.group.release', defaultMessage: 'Release' },
  promote: { id: 'app.sml.plans.group.promote', defaultMessage: 'Promote' },
  measure: { id: 'app.sml.plans.group.measure', defaultMessage: 'Measure' },
  team: { id: 'app.sml.plans.group.team', defaultMessage: 'Team' },
});

export const featureGroupMessages: Record<Music24FeatureGroup, MessageDescriptor> = groupMessages;

export interface Music24FeatureRow {
  key: string;
  group: Music24FeatureGroup;
  label: MessageDescriptor;
  value: (capabilities: Music24Capabilities, intl: IntlShape) => string;
}

/** Row labels for the plan compare list. IDs are static literals so `pnpm trans` can extract them. */
const rowMessages = defineMessages({
  // QA 2026-10-28 skp-4: Free publishes 3 SmartLink pages but makes no pre-save (`canUsePresave`).
  // The native apps read this id too (scripts/music24-native/i18n-keys.txt), so it keeps its id.
  pages: { id: 'app.sml.plans.row.pages', defaultMessage: 'Published SmartLink pages' },
  presaveServices: { id: 'app.sml.plans.row.presaveServices', defaultMessage: 'Pre-save services' },
  fanExport: { id: 'app.sml.plans.row.fanExport', defaultMessage: 'Pre-save fan email export' },
  customDomain: { id: 'app.sml.plans.row.customDomain', defaultMessage: 'Custom domain' },
  artists: { id: 'app.sml.plans.row.artists', defaultMessage: 'Artists' },
  releases: { id: 'app.sml.plans.row.releases', defaultMessage: 'Songs in promotion' },
  oldSongs: { id: 'app.sml.plans.row.oldSongs', defaultMessage: 'Push an old song again' },
  kitLook: { id: 'app.sml.plans.row.kitLook', defaultMessage: 'Your look: colours, logo, font' },
  kitZip: { id: 'app.sml.plans.row.kitZip', defaultMessage: 'Download the whole kit' },
  quality: { id: 'app.sml.plans.row.quality', defaultMessage: 'Images and videos' },
  fullVideo: {
    id: 'app.sml.plans.row.fullVideo',
    defaultMessage: 'Full-song videos and motion artwork',
  },
  aiClips: { id: 'app.sml.plans.row.aiClips', defaultMessage: 'AI clips for your music video' },
  renderLane: { id: 'app.sml.plans.row.renderLane', defaultMessage: 'Render queue' },
  earnings: { id: 'app.sml.plans.row.earnings', defaultMessage: 'Earnings' },
  streamingSplit: { id: 'app.sml.plans.row.streamingSplit', defaultMessage: 'Streams by store and country' },
  // QA 2026-10-28 skp-6: MCP reads and writes on every plan (an approved client may make, post and
  // edit; the plan limits of each route apply).
  mcp: { id: 'app.sml.plans.row.mcpRun', defaultMessage: 'Run MUSIC24 from your AI assistant (MCP)' },
  seats: { id: 'app.sml.plans.row.seats', defaultMessage: 'Team seats' },
  socialAccounts: { id: 'app.sml.plans.row.socialAccounts', defaultMessage: 'Connected social accounts' },
  x: { id: 'app.sml.plans.row.x', defaultMessage: 'Post to X' },
  scheduledPosts: { id: 'app.sml.plans.row.scheduledPosts', defaultMessage: 'Scheduled auto-posts' },
  videoCredits: { id: 'app.sml.plans.row.videoCredits', defaultMessage: 'Video credits' },
  aiInsights: { id: 'app.sml.plans.row.aiInsights', defaultMessage: 'AI insights' },
  outreachPitchesPerDay: { id: 'app.sml.plans.row.outreachPitchesPerDay', defaultMessage: 'New pitches a day' },
  outreachMailboxes: { id: 'app.sml.plans.row.outreachMailboxes', defaultMessage: 'Own mailboxes' },
  outreachAutopilot: { id: 'app.sml.plans.row.outreachAutopilot', defaultMessage: 'Autopilot' },
  outreachFollowUps: {
    id: 'app.sml.plans.row.outreachFollowUps',
    defaultMessage: 'Follow-ups, replies, adds found',
  },
  history: { id: 'app.sml.plans.row.history', defaultMessage: 'Playlist data history' },
  credits: { id: 'app.sml.plans.row.credits', defaultMessage: 'Daily browse credits' },
  follows: { id: 'app.sml.plans.row.follows', defaultMessage: 'Followed artists / tracks' },
  alerts: { id: 'app.sml.plans.row.alerts', defaultMessage: 'Playlist-add and mover alerts' },
  portalData: { id: 'app.sml.plans.row.portalData', defaultMessage: 'Spotify for Artists and Deezer data' },
  geography: { id: 'app.sml.plans.row.geography', defaultMessage: 'Markets and geo filters' },
  listeningInsights: { id: 'app.sml.plans.row.listeningInsights', defaultMessage: 'Listening Score insights' },
  csvExport: { id: 'app.sml.plans.row.csvExport', defaultMessage: 'Analytics CSV export' },
  serviceFilter: { id: 'app.sml.plans.row.serviceFilter', defaultMessage: 'Music-service filters' },
  api: { id: 'app.sml.plans.row.api', defaultMessage: 'API tokens' },
});

/** Row values for the plan compare list. IDs are static literals so `pnpm trans` can extract them. */
const valueMessages = defineMessages({
  unlimited: { id: 'app.sml.plans.value.unlimited', defaultMessage: 'Unlimited' },
  none: { id: 'app.sml.plans.value.none', defaultMessage: '—' },
  yes: { id: 'app.sml.plans.value.yes', defaultMessage: 'Included' },
  perMonth: { id: 'app.sml.plans.value.perMonth', defaultMessage: '{count} a month' },
  // The native apps still read this id (scripts/music24-native/i18n-keys.txt). Keep it until they stop.
  perMonthWatermarked: {
    id: 'app.sml.plans.value.perMonthWatermarked',
    defaultMessage: '{count} a month, watermarked',
  },
  perArtistPerMonth: { id: 'app.sml.plans.value.perArtistPerMonth', defaultMessage: '{count} per artist a month' },
  perArtist: { id: 'app.sml.plans.value.perArtist', defaultMessage: '{count} per artist' },
  artistsUpTo: { id: 'app.sml.plans.value.artistsUpTo', defaultMessage: '{included} included, up to {max}' },
  artistsNoCap: { id: 'app.sml.plans.value.artistsNoCap', defaultMessage: '{included} included, add more' },
  songsAtOnce: {
    id: 'app.sml.plans.value.songsAtOnce',
    defaultMessage: '{count, plural, one {# song} other {# songs}} at a time',
  },
  previewQuality: { id: 'app.sml.plans.value.previewQuality', defaultMessage: 'Low-res preview, watermarked' },
  fullQuality: { id: 'app.sml.plans.value.fullQuality', defaultMessage: 'Full quality' },
  laneFirst: { id: 'app.sml.plans.value.laneFirst', defaultMessage: 'First' },
  laneNormal: { id: 'app.sml.plans.value.laneNormal', defaultMessage: 'Normal' },
  laneLast: { id: 'app.sml.plans.value.laneLast', defaultMessage: 'Last' },
  days: { id: 'app.sml.plans.value.days', defaultMessage: '{days, plural, one {# day} other {# days}}' },
  oneYear: { id: 'app.sml.plans.value.oneYear', defaultMessage: '1 year' },
  weeklyDigest: { id: 'app.sml.plans.value.weeklyDigest', defaultMessage: 'Weekly digest' },
  realtime: { id: 'app.sml.plans.value.realtime', defaultMessage: 'Real time' },
  lastDays: { id: 'app.sml.plans.value.lastDays', defaultMessage: 'Last {days} days' },
  fullHistory: { id: 'app.sml.plans.value.fullHistory', defaultMessage: 'Full history' },
  // The native apps still read this id (scripts/music24-native/i18n-keys.txt). Keep it until they stop.
  allServices: { id: 'app.sml.plans.value.allServices', defaultMessage: 'Spotify, Apple Music, Deezer' },
  // The services of `defaultPresaveServices` (fym-shared `smartlinks/presave.ts`): the pre-save page
  // offers these five. A test pins the two lists together.
  presaveServices: {
    id: 'app.sml.plans.value.presaveServices',
    defaultMessage: 'Spotify, Apple Music, Tidal, Deezer, Amazon Music',
  },
  perDay: { id: 'app.sml.plans.value.perDay', defaultMessage: '{count} a day' },
  follows: { id: 'app.sml.plans.value.follows', defaultMessage: '{artists} artists / {tracks} tracks' },
});

/** The text a row shows for a plain "included". The plan cards then show the label alone. */
export function featureRowIncludedText(intl: IntlShape): string {
  return intl.formatMessage(valueMessages.yes);
}

/** The text a row shows for "not included". The compare table greys these cells out. */
export function featureRowNoneText(intl: IntlShape): string {
  return intl.formatMessage(valueMessages.none);
}

function boolValue(value: boolean, intl: IntlShape): string {
  return intl.formatMessage(value ? valueMessages.yes : valueMessages.none);
}

/** `null` means unlimited; a real number is formatted with the visitor's locale. */
function numberOrUnlimited(value: number | null, intl: IntlShape): string {
  return value === null ? intl.formatMessage(valueMessages.unlimited) : intl.formatNumber(value);
}

/** `null` means unlimited, `0` means none, otherwise "{count} a month". */
function perMonthValue(value: number | null, intl: IntlShape): string {
  if (value === null) {
    return intl.formatMessage(valueMessages.unlimited);
  }
  if (value === 0) {
    return intl.formatMessage(valueMessages.none);
  }
  return intl.formatMessage(valueMessages.perMonth, { count: value });
}

/** A solo plan: the number. A team plan: the included artists and the cap of the paid extra artists. */
function artistsValue(c: Music24Capabilities, intl: IntlShape): string {
  if (c.extraArtistLimit === null) {
    return intl.formatMessage(valueMessages.artistsNoCap, { included: c.includedArtists });
  }
  if (c.extraArtistLimit > 0) {
    return intl.formatMessage(valueMessages.artistsUpTo, {
      included: c.includedArtists,
      max: c.includedArtists + c.extraArtistLimit,
    });
  }
  return numberOrUnlimited(c.artistsLimit, intl);
}

/**
 * The new pitches a day of a plan (Outreach v2, design outreach-v2 l2): per artist on a team plan,
 * else the number; 0 is none. Follow-ups do not count.
 */
function outreachPitchesPerDayValue(c: Music24Capabilities, intl: IntlShape): string {
  if (c.outreachPitchesPerDay === 0) {
    return featureRowNoneText(intl);
  }
  if (isMusic24TeamPlanTier(c.plan)) {
    return intl.formatMessage(valueMessages.perArtist, { count: c.outreachPitchesPerDay });
  }
  return intl.formatNumber(c.outreachPitchesPerDay);
}

/** The own Outreach mailboxes: per artist on a team plan, else the number; 0 is none. */
function outreachMailboxesValue(c: Music24Capabilities, intl: IntlShape): string {
  if (c.outreachMailboxes === 0) {
    return featureRowNoneText(intl);
  }
  if (isMusic24TeamPlanTier(c.plan)) {
    return intl.formatMessage(valueMessages.perArtist, { count: c.outreachMailboxes });
  }
  return intl.formatNumber(c.outreachMailboxes);
}

const LANE_MESSAGES: Record<RenderLane, MessageDescriptor> = {
  first: valueMessages.laneFirst,
  normal: valueMessages.laneNormal,
  last: valueMessages.laneLast,
};

/**
 * One row per line a customer reads on the plan picker, grouped Release, Promote, Measure, Team
 * (design a), in that order. Every number comes straight from `Music24Capabilities`. No plan has
 * its own copy of these numbers; change a limit in `music24PlanCapabilities` and every row follows.
 */
export const MUSIC24_FEATURE_ROWS: Music24FeatureRow[] = [
  // Release
  {
    key: 'pages',
    group: 'release',
    label: rowMessages.pages,
    value: (c, intl) => numberOrUnlimited(c.publishedPagesLimit, intl),
  },
  {
    key: 'presaveServices',
    group: 'release',
    label: rowMessages.presaveServices,
    // Pre-saves and Get every release come with Artist and up (Pre-save Subscriptions, D4).
    value: (c, intl) =>
      c.canUsePresave ? intl.formatMessage(valueMessages.presaveServices) : featureRowNoneText(intl),
  },
  {
    key: 'fanExport',
    group: 'release',
    label: rowMessages.fanExport,
    value: (c, intl) => boolValue(c.canExportFans, intl),
  },
  {
    key: 'customDomain',
    group: 'release',
    label: rowMessages.customDomain,
    value: (c, intl) => boolValue(c.canUseCustomDomain, intl),
  },
  {
    key: 'artists',
    group: 'release',
    label: rowMessages.artists,
    value: (c, intl) => artistsValue(c, intl),
  },
  {
    key: 'releases',
    group: 'release',
    label: rowMessages.releases,
    value: (c, intl) =>
      c.activeReleasesLimit === null
        ? intl.formatMessage(valueMessages.unlimited)
        : intl.formatMessage(valueMessages.songsAtOnce, { count: c.activeReleasesLimit }),
  },
  {
    key: 'oldSongs',
    group: 'release',
    label: rowMessages.oldSongs,
    value: (c, intl) => boolValue(c.canPushOldSongs, intl),
  },
  {
    key: 'kitLook',
    group: 'release',
    label: rowMessages.kitLook,
    value: (c, intl) => boolValue(c.canCustomizeKitLook, intl),
  },
  {
    key: 'kitZip',
    group: 'release',
    label: rowMessages.kitZip,
    value: (c, intl) => boolValue(c.canDownloadKitZip, intl),
  },
  // Promote
  {
    key: 'socialAccounts',
    group: 'promote',
    label: rowMessages.socialAccounts,
    value: (c, intl) => numberOrUnlimited(c.socialAccountsLimit, intl),
  },
  {
    key: 'x',
    group: 'promote',
    label: rowMessages.x,
    value: (c, intl) => (c.canUseX ? perMonthValue(c.xPostsPerMonth, intl) : boolValue(false, intl)),
  },
  {
    key: 'scheduledPosts',
    group: 'promote',
    label: rowMessages.scheduledPosts,
    value: (c, intl) => perMonthValue(c.scheduledPostsPerMonth, intl),
  },
  {
    key: 'videoCredits',
    group: 'promote',
    label: rowMessages.videoCredits,
    value: (c, intl) =>
      c.videoCreditsPerArtist === null
        ? perMonthValue(c.videoCreditsPerMonth, intl)
        : intl.formatMessage(valueMessages.perArtistPerMonth, { count: c.videoCreditsPerArtist }),
  },
  {
    key: 'quality',
    group: 'promote',
    label: rowMessages.quality,
    value: (c, intl) => intl.formatMessage(c.previewRenders ? valueMessages.previewQuality : valueMessages.fullQuality),
  },
  {
    key: 'fullVideo',
    group: 'promote',
    label: rowMessages.fullVideo,
    value: (c, intl) => boolValue(c.canRenderFullSongVideos, intl),
  },
  {
    key: 'aiClips',
    group: 'promote',
    label: rowMessages.aiClips,
    // The AI clips go into the music video, a full-song video (QA 2026-10-28 skp-4): Plus has the AI
    // footage flag but no music video editor, as music24.com/pricing says.
    value: (c, intl) => boolValue(c.canMakeAiFootage && c.canRenderFullSongVideos, intl),
  },
  {
    key: 'renderLane',
    group: 'promote',
    label: rowMessages.renderLane,
    value: (c, intl) => intl.formatMessage(LANE_MESSAGES[c.renderLane]),
  },
  {
    key: 'aiInsights',
    group: 'promote',
    label: rowMessages.aiInsights,
    value: (c, intl) => perMonthValue(c.aiInsightsPerMonth, intl),
  },
  {
    key: 'outreachPitchesPerDay',
    group: 'promote',
    label: rowMessages.outreachPitchesPerDay,
    value: (c, intl) => outreachPitchesPerDayValue(c, intl),
  },
  {
    key: 'outreachMailboxes',
    group: 'promote',
    label: rowMessages.outreachMailboxes,
    value: (c, intl) => outreachMailboxesValue(c, intl),
  },
  {
    key: 'outreachAutopilot',
    group: 'promote',
    label: rowMessages.outreachAutopilot,
    value: (c, intl) => boolValue(c.canUseOutreachAutopilot, intl),
  },
  {
    key: 'outreachFollowUps',
    group: 'promote',
    label: rowMessages.outreachFollowUps,
    value: (c, intl) => boolValue(c.outreachPitchesPerDay > 0, intl),
  },
  // Measure
  {
    key: 'history',
    group: 'measure',
    label: rowMessages.history,
    value: (c, intl) =>
      c.historyDaysLimit === 365
        ? intl.formatMessage(valueMessages.oneYear)
        : intl.formatMessage(valueMessages.days, { days: c.historyDaysLimit }),
  },
  {
    key: 'credits',
    group: 'measure',
    label: rowMessages.credits,
    value: (c, intl) => intl.formatMessage(valueMessages.perDay, { count: c.dailyBrowseCredits }),
  },
  {
    key: 'follows',
    group: 'measure',
    label: rowMessages.follows,
    value: (c, intl) =>
      intl.formatMessage(valueMessages.follows, { artists: c.artistsFollowLimit, tracks: c.tracksFollowLimit }),
  },
  {
    key: 'alerts',
    group: 'measure',
    label: rowMessages.alerts,
    value: (c, intl) => intl.formatMessage(c.realtimeAlerts ? valueMessages.realtime : valueMessages.weeklyDigest),
  },
  {
    key: 'portalData',
    group: 'measure',
    label: rowMessages.portalData,
    value: (c, intl) =>
      c.artistPortalHistoryDays === null
        ? intl.formatMessage(valueMessages.fullHistory)
        : intl.formatMessage(valueMessages.lastDays, { days: c.artistPortalHistoryDays }),
  },
  {
    key: 'earnings',
    group: 'measure',
    label: rowMessages.earnings,
    value: (c, intl) => boolValue(c.canSeeEarnings, intl),
  },
  {
    key: 'streamingSplit',
    group: 'measure',
    label: rowMessages.streamingSplit,
    value: (c, intl) => boolValue(c.canSplitStreaming, intl),
  },
  {
    key: 'geography',
    group: 'measure',
    label: rowMessages.geography,
    value: (c, intl) => boolValue(c.canFilterGeographically, intl),
  },
  {
    key: 'listeningInsights',
    group: 'measure',
    label: rowMessages.listeningInsights,
    value: (c, intl) => boolValue(c.canViewListeningInsights, intl),
  },
  {
    key: 'csvExport',
    group: 'measure',
    label: rowMessages.csvExport,
    value: (c, intl) => boolValue(c.canExport, intl),
  },
  {
    key: 'serviceFilter',
    group: 'measure',
    label: rowMessages.serviceFilter,
    value: (c, intl) => boolValue(c.canFilterByMusicService, intl),
  },
  // Team
  {
    key: 'seats',
    group: 'team',
    label: rowMessages.seats,
    value: (c, intl) => numberOrUnlimited(c.seatsLimit, intl),
  },
  {
    key: 'api',
    group: 'team',
    label: rowMessages.api,
    value: (c, intl) => boolValue(c.canAccessApi, intl),
  },
  {
    key: 'mcp',
    group: 'team',
    label: rowMessages.mcp,
    // MCP reads and writes on every plan (music24 skill §3, owner decision 2026-10-10).
    value: (_c, intl) => boolValue(true, intl),
  },
];

/** The rows of one group, in table order. Every row shows to every workspace. */
export function featureRowsInGroup(group: Music24FeatureGroup): Music24FeatureRow[] {
  return MUSIC24_FEATURE_ROWS.filter((row) => row.group === group);
}
